// Deep Signal "Actions" tab: the living action list. Every figure, title,
// verdict and status comes from the server (app/services/action_engine.py and
// action_list.py); this component only lays it out and filters it.
//
// The UI is built around each item's classification, the verdict of the read
// that last touched it: new, updated, carried, confirmed, not yet moved,
// retired. The "What changed in Read N" strip counts this read's verdicts and
// each count is a filter. Clients see percentages; admins also see counts,
// the impact components, and the admin controls (title, effort, pin, hide,
// publish, retire, restore, content items, edit log).

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  Download, ChevronDown, ChevronRight, Pin, PinOff, EyeOff, Eye, X, Pencil, Plus, Send, History, Archive, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getActionList, downloadActionListExport, editAction, retireAction, restoreAction,
  publishAction, getActionLog, addActionItem, editActionItem, publishActionItem,
} from '@/api/action-list';
import type {
  ActionCell, ActionClassification, ActionClassificationKind, ActionEffort, ActionEvent,
  ActionItem, ActionListView, ItemKind, LivingAction,
} from '@/api/action-list';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';
import { WriteFromAction } from '../../content/content-draft-panel';
import { listPublications } from '@/api/content';
import type { Publication } from '@/api/content';
import './action-card.css';

const MUTED = 'var(--muted-foreground)';
const TEAL = 'var(--alpha-teal)';

const CLASS_COLORS: Record<ActionClassificationKind, { bg: string; fg: string }> = {
  new: { bg: TEAL, fg: 'var(--background)' },
  updated: { bg: 'var(--info-bg, var(--muted))', fg: 'var(--info-text, var(--foreground))' },
  carried: { bg: 'var(--muted)', fg: 'var(--muted-foreground)' },
  confirmed: { bg: 'var(--success-bg)', fg: 'var(--success-text)' },
  not_yet_moved: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)' },
  retired: { bg: 'var(--muted)', fg: 'var(--muted-foreground)' },
};
// Strip order: the four the read most often changes first.
const STRIP_ORDER: ActionClassificationKind[] = ['new', 'updated', 'confirmed', 'retired', 'not_yet_moved', 'carried'];
const STRIP_LABELS: Record<ActionClassificationKind, string> = {
  new: 'Added', updated: 'Updated', confirmed: 'Confirmed', retired: 'Retired',
  not_yet_moved: 'Not yet moved', carried: 'Carried',
};
const ITEM_KINDS: ItemKind[] = ['draft', 'page', 'asset', 'section', 'dataset', 'comparison', 'story'];
const OPEN = new Set(['new', 'carried', 'in_progress']);

function pct(cell: ActionCell | null | undefined): string {
  if (!cell || cell.share == null) return 'n/a';
  return `${Math.round(cell.share * 100)}%`;
}

function Pct({ cell }: { cell: ActionCell | null | undefined }) {
  const title = cell && cell.answers != null ? `${cell.present} of ${cell.answers} answers` : undefined;
  return (
    <span title={title} style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
      {pct(cell)}
      {cell?.directional && <span style={{ color: MUTED }} title="Directional: fewer than 20 answers">*</span>}
    </span>
  );
}

function Badge({ children, bg, fg, title, onClick }: {
  children: ReactNode; bg: string; fg: string; title?: string; onClick?: () => void;
}) {
  return (
    <span title={title} onClick={onClick} role={onClick ? 'button' : undefined} style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--text-xs)', fontWeight: 600,
      padding: '2px 8px', borderRadius: '999px', backgroundColor: bg, color: fg, whiteSpace: 'nowrap',
      cursor: onClick ? 'pointer' : undefined,
    }}>
      {children}
    </span>
  );
}

const btn: CSSProperties = {
  fontSize: 'var(--text-xs)', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)',
  backgroundColor: 'var(--background)', color: 'var(--foreground)', cursor: 'pointer',
};
const input: CSSProperties = {
  fontSize: 'var(--text-sm)', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)',
  backgroundColor: 'var(--background)', color: 'var(--foreground)', width: '100%',
};
const select: CSSProperties = { ...btn, paddingRight: '4px' };

/** One titled block of an action card, set off from the one above it. */
function CardSection({ label, children }: { label?: ReactNode; children: ReactNode }) {
  return (
    <div className="ds-act-section">
      {label && <div className="ds-act-section__label">{label}</div>}
      {children}
    </div>
  );
}

/** A small inline form used for publish, retire, title and items. */
function InlineForm({ fields, submitLabel, onSubmit, onCancel }: {
  fields: { key: string; placeholder: string; initial?: string; options?: string[] }[];
  submitLabel: string;
  onSubmit: (values: Record<string, string>) => Promise<void>;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(fields.map(f => [f.key, f.initial ?? (f.options ? f.options[0] : '')])),
  );
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px', maxWidth: '560px' }}>
      {fields.map(f => f.options ? (
        <select key={f.key} style={{ ...input }} value={values[f.key]}
          onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}>
          {f.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input key={f.key} style={input} placeholder={f.placeholder} value={values[f.key]}
          onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))} />
      ))}
      <div style={{ display: 'flex', gap: '6px' }}>
        <button className="ds-act-btn ds-act-btn--primary" disabled={busy} onClick={async () => {
          setBusy(true);
          try { await onSubmit(values); } finally { setBusy(false); }
        }}>{submitLabel}</button>
        <button className="ds-act-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** Pick the publication record that says this went live (#133): "published"
 *  is set by a record, from Brand Publisher or entered by hand on the Brand
 *  record page, never by a button alone. */
function PublicationPicker({ customerId, actionId, itemId, submitLabel, onSubmit, onCancel }: {
  customerId: number; actionId: number; itemId?: number; submitLabel: string;
  onSubmit: (publicationId: number) => Promise<void>; onCancel: () => void;
}) {
  const [pubs, setPubs] = useState<Publication[] | null>(null);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    listPublications(customerId).then(r => {
      const mine = r.publications.filter(p => (p.action_id == null || p.action_id === actionId)
        && (itemId == null || p.action_item_id == null || p.action_item_id === itemId));
      setPubs(mine);
      if (mine[0]) setChosen(String(mine[0].id));
    }).catch(() => setPubs([]));
  }, [customerId, actionId, itemId]);
  if (pubs == null) return <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '8px' }}>Loading publication records…</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px', maxWidth: '560px' }}>
      {pubs.length === 0 ? (
        <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>
          No publication record for this action yet. Publish through Brand Publisher, or record it by hand on the
          Brand record page (Record a publication), then come back.
        </p>
      ) : (
        <select style={{ ...input }} value={chosen} onChange={e => setChosen(e.target.value)}>
          {pubs.map(p => (
            <option key={p.id} value={p.id}>
              #{p.id} · {p.destination} · {p.title || p.url || 'untitled'} · {new Date(p.published_at).toLocaleDateString()}
            </option>
          ))}
        </select>
      )}
      <div style={{ display: 'flex', gap: '6px' }}>
        <button className="ds-act-btn ds-act-btn--primary" disabled={busy || !chosen} onClick={async () => {
          setBusy(true);
          try { await onSubmit(Number(chosen)); } finally { setBusy(false); }
        }}>{submitLabel}</button>
        <button className="ds-act-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ClassBadge({ c, onClick }: { c: ActionClassification; onClick?: () => void }) {
  const colors = CLASS_COLORS[c.kind];
  const title = [c.read_label ? `Set by ${c.read_label}` : null, c.by === 'admin' ? 'by an admin' : null,
    ...c.reasons].filter(Boolean).join(' · ');
  return <Badge bg={colors.bg} fg={colors.fg} title={title || undefined} onClick={onClick}>{c.badge}</Badge>;
}

function ItemRow({ item, isAdmin, customerId, actionId, onChanged }: {
  item: ActionItem; isAdmin: boolean; customerId: number; actionId: number; onChanged: () => void;
}) {
  const [linking, setLinking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const run = async (fn: () => Promise<unknown>, done: string) => {
    try { await fn(); toast.success(done); setLinking(false); setPublishing(false); onChanged(); }
    catch (e: any) { toast.error(e?.message || 'Could not save'); }
  };
  return (
    <li style={{ marginBottom: '8px' }}>
      <span style={{ fontWeight: 600 }}>{item.kind}</span>: {item.url
        ? <a href={item.url} target="_blank" rel="noreferrer" style={{ color: TEAL }}>{item.title}</a>
        : item.title}{' '}
      {item.stages.map((st, i) => (
        <span key={i} style={{ color: st.stage === item.classification ? 'var(--foreground)' : MUTED }}>
          {i > 0 && ' → '}
          {st.stage}
          {st.read_label ? ` (${st.read_label})` : ''}
          {st.verdict ? ` ${st.verdict}` : ''}
        </span>
      ))}
      {isAdmin && (
        <span style={{ marginLeft: '8px', display: 'inline-flex', gap: '6px', alignItems: 'center', verticalAlign: 'middle' }}>
          {item.classification !== 'published' && item.classification !== 'measured' && (
            <>
              <button className="ds-act-btn" aria-pressed={linking} onClick={() => setLinking(l => !l)}>
                {item.wind_tunnel ? 'Change wind tunnel run' : 'Link wind tunnel run'}
              </button>
              <button className="ds-act-btn" aria-pressed={publishing} onClick={() => setPublishing(p => !p)}
                title="Published is set by a publication record">
                Mark published
              </button>
            </>
          )}
          {item.wind_tunnel && !item.tested && (
            <span style={{ color: MUTED }}>wind tunnel run #{item.wind_tunnel.run_id} {item.wind_tunnel.status ?? ''}</span>
          )}
        </span>
      )}
      {linking && (
        <InlineForm
          fields={[{ key: 'run', placeholder: 'Wind tunnel run id' },
                   { key: 'draft', placeholder: 'Draft id, e.g. d2 (optional)' }]}
          submitLabel="Link"
          onSubmit={v => run(() => editActionItem(customerId, actionId, item.id, {
            wind_tunnel_run_id: v.run ? Number(v.run) : null, wind_tunnel_draft_id: v.draft || null,
          }), 'Linked')}
          onCancel={() => setLinking(false)}
        />
      )}
      {publishing && (
        <PublicationPicker customerId={customerId} actionId={actionId} itemId={item.id} submitLabel="Mark published"
          onSubmit={pid => run(() => publishActionItem(customerId, actionId, item.id, pid), 'Published')}
          onCancel={() => setPublishing(false)} />
      )}
    </li>
  );
}

function ActionCard({ a, isAdmin, customerId, onChanged, onOpenRead }: {
  a: LivingAction;
  isAdmin: boolean;
  customerId: number;
  onChanged: () => void;
  onOpenRead?: (runId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [focusRun, setFocusRun] = useState<number | null>(null);
  const [form, setForm] = useState<null | 'title' | 'publish' | 'retire' | 'item'>(null);
  const [log, setLog] = useState<ActionEvent[] | null>(null);
  const ev = a.latest_evidence;
  const isOpen = OPEN.has(a.status);

  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast.success(done);
      setForm(null);
      onChanged();
    } catch (e: any) {
      toast.error(e?.message || 'Could not save');
    }
  };

  // A badge opens the evidence at the read that set it.
  const openAt = (runId: number | null) => {
    setOpen(true);
    setFocusRun(runId);
  };

  const impactTitle = a.impact_components
    ? `headroom ${a.impact_components.headroom} × conversion ${a.impact_components.conversion} × reachability ${a.impact_components.reachability} × trend ${a.impact_components.trend}`
    : 'Headroom × conversion × reachability × trend, recomputed on every read';

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: '10px', padding: '16px 18px',
      backgroundColor: 'var(--background)', opacity: a.hidden ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        {a.position != null && (
          <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: MUTED, minWidth: '24px' }}>{a.position}</span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
            <ClassBadge c={a.classification} onClick={() => openAt(a.classification.run_id)} />
            {a.pinned && <Badge bg="var(--muted)" fg="var(--foreground)"><Pin size={11} /> Pinned</Badge>}
            {a.hidden && <Badge bg="var(--muted)" fg={MUTED}><EyeOff size={11} /> Hidden from client</Badge>}
            {a.precedent.has_precedent
              ? <Badge bg="var(--success-bg)" fg="var(--success-text)"
                  title={a.precedent.precedents?.length
                    ? a.precedent.precedents.map(p => p.line).join('\n')
                    : `${a.precedent.count} published piece${a.precedent.count === 1 ? '' : 's'} moved a belief like this one (${a.precedent.belief_category})`}>
                  Has precedent{a.precedent.count && a.precedent.count > 1 ? ` (${a.precedent.count})` : ''}
                </Badge>
              : <Badge bg="var(--muted)" fg={MUTED} title="No published piece has moved a belief like this one yet">Experimental</Badge>}
            <span style={{ fontSize: 'var(--text-xs)', color: MUTED }}>{a.generator_label} · {a.status_label}</span>
          </div>
          <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', lineHeight: 1.35 }}>{a.title}</p>
          {(a.publications ?? []).map(p => (
            <p key={p.publication_id} style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '4px' }} title={p.note}>
              Publication “{p.title}”: <b style={{ color: p.status === 'moved' ? 'var(--success-text)' : p.status === 'reversed' ? 'var(--destructive)' : 'var(--foreground)' }}>
                {p.status_label}{p.resolved_at_label ? ` at ${p.resolved_at_label}` : ''}
                {p.lasting === 'held' || p.lasting === 'faded' ? `, ${p.lasting} after` : ''}
              </b>
            </p>
          ))}
          {a.classification.kind === 'updated' && a.classification.reasons.length > 0 && (
            <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '4px' }}>
              Updated: {a.classification.reasons.join('; ')}
            </p>
          )}
          {ev && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', marginTop: '6px', lineHeight: 1.5 }}>
              {ev.statement} <span style={{ color: MUTED }}>({ev.read_label})</span>
            </p>
          )}
          {ev?.quote && (
            <blockquote style={{
              margin: '8px 0 0', padding: '6px 12px', borderLeft: `3px solid ${TEAL}`,
              fontSize: 'var(--text-sm)', color: 'var(--foreground)', fontStyle: 'italic',
            }}>
              “{ev.quote.text}” <span style={{ fontStyle: 'normal', color: MUTED }}>({ev.quote.model_label})</span>
            </blockquote>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '10px', fontSize: 'var(--text-xs)', color: MUTED }}>
            {a.impact_score != null && <span title={impactTitle}>Impact <b style={{ color: 'var(--foreground)' }}>{a.impact_score}</b></span>}
            {a.target_now && <span>Target now <Pct cell={a.target_now} /></span>}
            {a.effort && <span>Effort <b style={{ color: 'var(--foreground)' }}>{a.effort}</b></span>}
            {a.what_it_moves.metric_label && (
              <span>
                Moves {a.what_it_moves.metric_label}
                {a.what_it_moves.lanes_first?.length ? `, on ${a.what_it_moves.lanes_first.map(l => l.label).join(', ')} first` : ''}
                {a.what_it_moves.checked_at_label ? `; checked at ${a.what_it_moves.checked_at_label}` : ''}
              </span>
            )}
            {a.source_models.length > 0 && <span>Source models: {a.source_models.map(m => m.label).join(', ')}</span>}
          </div>
          {a.gap_status && (
            <p style={{ fontSize: 'var(--text-xs)', marginTop: '6px',
                        color: a.gap_status.status === 'persisting' ? 'var(--infrared-coral)'
                          : a.gap_status.status === 'fixed' ? 'var(--success-text)' : MUTED }}>
              Gap: <b>{a.gap_status.label}</b>
              {a.gap_status.status === 'persisting' && a.gap_status.reads_elapsed
                ? `, still contradicted ${a.gap_status.reads_elapsed} read${a.gap_status.reads_elapsed === 1 ? '' : 's'} after the fix was published`
                : a.gap_status.status === 'fixed' ? ', a later read shows no answer contradicting it' : ''}
            </p>
          )}
          {a.status_note && a.classification.kind !== 'updated' && (
            <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '6px' }}>{a.status_note}</p>
          )}
          {a.confirmation && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--success-text)', marginTop: '6px' }}>
              Moved <Pct cell={a.confirmation.before.cell} /> in {a.confirmation.before.read_label} →{' '}
              <Pct cell={a.confirmation.after.cell} /> in {a.confirmation.after.read_label}
            </p>
          )}
          {a.published && (
            <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '6px' }}>
              Published {new Date(a.published.at).toLocaleDateString()}{a.published.after_read ? ` (after ${a.published.after_read})` : ''}: {a.published.note}
              {a.published.url && <> · <a href={a.published.url} target="_blank" rel="noreferrer" style={{ color: TEAL }}>link</a></>}
            </p>
          )}

          {ev?.builds && ev.builds.length > 0 && (
            <CardSection label="Proposed builds">
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: 1.55 }}>
                {ev.builds.map((b, i) => (
                  <li key={i}>
                    {b.text}
                    {b.url && <> <a href={b.url} target="_blank" rel="noreferrer" style={{ color: TEAL }}>open</a></>}
                  </li>
                ))}
              </ul>
            </CardSection>
          )}

          {a.items.length > 0 && (
            <CardSection label={`Content (${a.items.length})`}>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-xs)', color: 'var(--foreground)' }}>
                {a.items.map(item => (
                  <ItemRow key={item.id} item={item} isAdmin={isAdmin} customerId={customerId} actionId={a.id} onChanged={onChanged} />
                ))}
              </ul>
            </CardSection>
          )}

          {isAdmin && isOpen && (
            <CardSection label="Write from the brand record">
              <WriteFromAction customerId={customerId} actionId={a.id} isAdmin={isAdmin} />
            </CardSection>
          )}

          <CardSection>
            <button className="ds-act-link" aria-expanded={open} onClick={() => { setOpen(o => !o); setFocusRun(null); }}>
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              Verdicts and evidence by read ({a.classification_history.length})
            </button>
          {open && (
            <div style={{ marginTop: '8px', fontSize: 'var(--text-xs)', color: 'var(--foreground)' }}>
              <ol style={{ margin: 0, paddingLeft: '18px' }}>
                {a.classification_history.map((c, i) => {
                  const evidence = a.evidence.filter(e => e.run_id === c.run_id);
                  const focused = focusRun != null && focusRun === c.run_id;
                  return (
                    <li key={i} style={{
                      marginBottom: '6px', padding: focused ? '4px 6px' : undefined,
                      borderRadius: '6px', backgroundColor: focused ? 'var(--muted)' : undefined,
                    }}>
                      <ClassBadge c={c} />{' '}
                      <b>{c.read_label ?? 'Admin'}</b>
                      {c.run_id != null && onOpenRead && (
                        <> · <button onClick={() => onOpenRead(c.run_id as number)} style={{
                          border: 'none', background: 'none', padding: 0, color: TEAL, cursor: 'pointer', fontSize: 'inherit',
                        }}>open {c.read_label ?? 'read'}</button></>
                      )}
                      {c.reasons.length > 0 && <span style={{ color: MUTED }}> · {c.reasons.join('; ')}</span>}
                      {evidence.map((e, j) => (
                        <p key={j} style={{ margin: '3px 0 0' }}>
                          {e.statement}{' '}
                          {e.figures.length > 0 && (
                            <span style={{ color: MUTED }}>
                              ({e.figures.map((f, k) => <span key={k}>{k > 0 && ', '}{f.label} <Pct cell={f.cell} /></span>)})
                            </span>
                          )}
                        </p>
                      ))}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
          </CardSection>

          {isAdmin && (
            <div className="ds-act-admin">
              <div className="ds-act-section__label">Admin</div>
              <div className="ds-act-toolbar" role="toolbar" aria-label="Admin controls for this action">
                <button className="ds-act-btn" aria-pressed={form === 'title'}
                  onClick={() => setForm(form === 'title' ? null : 'title')}>
                  <Pencil size={13} /> Edit title
                </button>
                <select className="ds-act-btn" aria-label="Effort" value={a.effort ?? ''}
                  onChange={e => run(() => editAction(customerId, a.id, { effort: (e.target.value || null) as ActionEffort | null }), 'Effort saved')}>
                  <option value="">Effort: unset</option>
                  <option value="low">Effort: low</option>
                  <option value="medium">Effort: medium</option>
                  <option value="high">Effort: high</option>
                </select>

                <span className="ds-act-divider" aria-hidden="true" />

                <button className="ds-act-btn" aria-pressed={a.pinned}
                  onClick={() => run(() => editAction(customerId, a.id, { pinned: !a.pinned }), a.pinned ? 'Unpinned' : 'Pinned')}>
                  {a.pinned ? <><PinOff size={13} /> Unpin</> : <><Pin size={13} /> Pin</>}
                </button>
                <button className="ds-act-btn" aria-pressed={a.hidden}
                  onClick={() => run(() => editAction(customerId, a.id, { hidden: !a.hidden }), a.hidden ? 'Shown to client' : 'Hidden from client')}>
                  {a.hidden ? <><Eye size={13} /> Show to client</> : <><EyeOff size={13} /> Hide from client</>}
                </button>

                <span className="ds-act-divider" aria-hidden="true" />

                <button className="ds-act-btn" aria-pressed={form === 'item'}
                  onClick={() => setForm(form === 'item' ? null : 'item')}>
                  <Plus size={13} /> Add content
                </button>
                {isOpen && !a.published && (
                  <button className="ds-act-btn" aria-pressed={form === 'publish'}
                    onClick={() => setForm(form === 'publish' ? null : 'publish')}>
                    <Send size={13} /> Log publish
                  </button>
                )}

                <span className="ds-act-spacer" />

                <button className="ds-act-btn" aria-pressed={log != null} onClick={async () => {
                  if (log) { setLog(null); return; }
                  try { setLog((await getActionLog(customerId, a.id)).events); } catch (e: any) { toast.error(e?.message || 'Could not load the log'); }
                }}>
                  <History size={13} /> {log ? 'Hide log' : 'Log'}
                </button>
                {a.admin_retired && (
                  <button className="ds-act-btn" onClick={() => run(() => restoreAction(customerId, a.id), 'Restored')}>
                    <RotateCcw size={13} /> Restore
                  </button>
                )}
                {isOpen && (
                  <button className="ds-act-btn ds-act-btn--danger" aria-pressed={form === 'retire'}
                    onClick={() => setForm(form === 'retire' ? null : 'retire')}>
                    <Archive size={13} /> Retire
                  </button>
                )}
              </div>
              {form === 'title' && (
                <InlineForm
                  fields={[{ key: 'title', placeholder: a.generated_title ?? 'Title', initial: a.title === a.generated_title ? '' : a.title }]}
                  submitLabel="Save title (empty restores the generated one)"
                  onSubmit={v => run(() => editAction(customerId, a.id, { title_override: v.title || null }), 'Title saved')}
                  onCancel={() => setForm(null)}
                />
              )}
              {form === 'item' && (
                <InlineForm
                  fields={[{ key: 'kind', placeholder: 'Kind', options: ITEM_KINDS },
                           { key: 'title', placeholder: 'Title' },
                           { key: 'url', placeholder: 'https://... (optional)' },
                           { key: 'run', placeholder: 'Wind tunnel run id (optional)' },
                           { key: 'draft', placeholder: 'Wind tunnel draft id, e.g. d2 (optional)' }]}
                  submitLabel="Add content item"
                  onSubmit={v => run(() => addActionItem(customerId, a.id, {
                    kind: v.kind as ItemKind, title: v.title, url: v.url || undefined,
                    wind_tunnel_run_id: v.run ? Number(v.run) : null, wind_tunnel_draft_id: v.draft || null,
                  }), 'Content item added')}
                  onCancel={() => setForm(null)}
                />
              )}
              {form === 'publish' && (
                <PublicationPicker customerId={customerId} actionId={a.id} submitLabel="Add to action log"
                  onSubmit={pid => run(() => publishAction(customerId, a.id, pid), 'Added to the action log')}
                  onCancel={() => setForm(null)} />
              )}
              {form === 'retire' && (
                <InlineForm
                  fields={[{ key: 'reason', placeholder: 'Why it is retired' },
                           { key: 'superseded', placeholder: 'Superseded by action id (optional)' }]}
                  submitLabel="Retire"
                  onSubmit={v => run(() => retireAction(customerId, a.id, v.reason,
                    v.superseded ? Number(v.superseded) : null), 'Retired')}
                  onCancel={() => setForm(null)}
                />
              )}
              {log && (
                <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: 'var(--text-xs)', color: MUTED }}>
                  {log.length === 0 && <li>No events yet.</li>}
                  {log.map(e => (
                    <li key={e.id}>
                      {new Date(e.created_at).toLocaleString()} · {e.kind}{e.actor ? ` · ${e.actor}` : ''}{e.reason ? `: ${e.reason}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Collapsible({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div style={{ marginTop: '28px' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none', background: 'none', padding: 0,
        fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', cursor: 'pointer',
      }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} {title} ({count})
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>{children}</div>}
    </div>
  );
}

type Filters = {
  strip: ActionClassificationKind | null;   // this read's verdict (from the strip)
  classification: ActionClassificationKind | '';
  belief: string;
  generator: string;
  lane: string;
};
const NO_FILTERS: Filters = { strip: null, classification: '', belief: '', generator: '', lane: '' };

export function ActionListSection({ customerId, onOpenRead }: {
  customerId: number | null;
  /** Select a read on the Deep Signal page (a badge's read link). */
  onOpenRead?: (runId: number) => void;
}) {
  const [view, setView] = useState<ActionListView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sort, setSort] = useState<'rank' | 'new_first'>('rank');

  useEffect(() => {
    if (customerId == null) return;
    let cancelled = false;
    setError(null);
    getActionList(customerId)
      .then(v => { if (!cancelled) setView(v); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [customerId, nonce]);

  const all = useMemo(() => view ? [...view.open, ...view.confirmed, ...view.retired] : [], [view]);
  const options = useMemo(() => {
    const beliefs = new Map<string, string>();
    const generators = new Map<string, string>();
    const lanes = new Map<string, string>();
    for (const a of all) {
      if (a.belief) beliefs.set(a.belief.id, a.belief.name);
      generators.set(a.generator, a.generator_label);
      for (const l of a.lanes) lanes.set(l.model, l.label);
    }
    const sorted = (m: Map<string, string>) => [...m.entries()].sort((x, y) => x[1].localeCompare(y[1]));
    return { beliefs: sorted(beliefs), generators: sorted(generators), lanes: sorted(lanes) };
  }, [all]);

  if (customerId == null) return <DSEmptyState message="No client selected." />;
  if (error) return <DSEmptyState message={error} />;
  if (!view) return <div style={{ padding: '32px 64px' }}>{[1, 2, 3, 4].map(i => <DSLoadingRow key={i} />)}</div>;

  const isAdmin = view.viewer_is_admin;
  const asOf = view.as_of?.label ?? 'the latest read';
  const asOfRun = view.as_of?.run_id ?? null;
  const reload = () => setNonce(n => n + 1);
  const filtering = filters.strip != null || filters.classification !== '' || filters.belief !== ''
    || filters.generator !== '' || filters.lane !== '';

  const matches = (a: LivingAction) =>
    (filters.strip == null || (a.classification.kind === filters.strip && a.classification.run_id === asOfRun))
    && (filters.classification === '' || a.classification.kind === filters.classification)
    && (filters.belief === '' || a.belief?.id === filters.belief)
    && (filters.generator === '' || a.generator === filters.generator)
    && (filters.lane === '' || a.lanes.some(l => l.model === filters.lane));

  // Without a filter the list is what this read left open, plus what it
  // closed; older confirmed/retired items sit in the history below.
  const setByThisRead = (a: LivingAction) => a.classification.run_id === asOfRun;
  const main = all.filter(a => filtering ? matches(a) : (OPEN.has(a.status) || setByThisRead(a)));
  const order = (a: LivingAction) => (OPEN.has(a.status) ? 0 : 1);
  const freshness = (a: LivingAction) =>
    setByThisRead(a) && a.classification.kind === 'new' ? 0 : setByThisRead(a) && a.classification.kind === 'updated' ? 1 : 2;
  main.sort((x, y) =>
    (sort === 'new_first' ? freshness(x) - freshness(y) : 0)
    || order(x) - order(y)
    || (x.position ?? 1e6) - (y.position ?? 1e6)
    || (y.impact_score ?? 0) - (x.impact_score ?? 0));
  const olderConfirmed = filtering ? [] : view.confirmed.filter(a => !setByThisRead(a));
  const olderRetired = filtering ? [] : view.retired.filter(a => !setByThisRead(a));

  const download = async (format: 'md' | 'json') => {
    try { await downloadActionListExport(customerId, format); } catch (e: any) { toast.error(e?.message || 'Export failed'); }
  };

  const byKind = new Map(view.summary.by_classification.map(c => [c.kind, c]));
  const card = (a: LivingAction) => (
    <ActionCard key={a.id} a={a} isAdmin={isAdmin} customerId={customerId} onChanged={reload} onOpenRead={onOpenRead} />
  );

  return (
    <div style={{ padding: '32px 64px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div>
          <SectionLabel>{`AS OF ${asOf.toUpperCase()}`}</SectionLabel>
          <SectionTitle size="xl">Action list</SectionTitle>
          <p style={{ fontSize: 'var(--text-sm)', color: MUTED, maxWidth: '720px' }}>
            Each action exists because of a figure in a specific read, and says which. Every read adds to this list,
            re-scores it, confirms published builds whose target moved, and retires gaps that closed on their own.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button style={btn} onClick={() => download('md')}><Download size={11} /> Markdown</button>
          <button style={btn} onClick={() => download('json')}><Download size={11} /> JSON</button>
        </div>
      </div>

      {/* What changed in Read N: this read's verdicts, each a filter. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px' }}>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '10px' }}>What changed in {asOf}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {STRIP_ORDER.map(kind => {
            const c = byKind.get(kind);
            const active = filters.strip === kind;
            const value = isAdmin ? String(c?.count ?? 0) : c?.share != null ? `${Math.round(c.share * 100)}%` : '0%';
            const colors = CLASS_COLORS[kind];
            return (
              <button key={kind}
                onClick={() => setFilters(f => ({ ...f, strip: active ? null : kind }))}
                title={isAdmin && c?.share != null ? `${Math.round(c.share * 100)}% of the ${view.summary.total} items this read classified` : undefined}
                style={{
                  ...btn, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                  borderColor: active ? TEAL : 'var(--border)', boxShadow: active ? `0 0 0 1px ${TEAL}` : undefined,
                }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: colors.bg, border: '1px solid var(--border)' }} />
                {STRIP_LABELS[kind]} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</b>
              </button>
            );
          })}
        </div>
        {view.changes.length > 0 && (
          <details style={{ marginTop: '10px' }}>
            <summary style={{ fontSize: 'var(--text-xs)', color: MUTED, cursor: 'pointer' }}>Details</summary>
            <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: 'var(--text-xs)' }}>
              {view.changes.map((c, i) => (
                <li key={i} style={{ marginBottom: '3px' }}>
                  <b>{c.label}:</b> {c.title}. <span style={{ color: MUTED }}>{c.reason}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {/* Filters and sort */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '16px' }}>
        <select style={select} value={filters.classification}
          onChange={e => setFilters(f => ({ ...f, classification: e.target.value as Filters['classification'] }))}>
          <option value="">All classifications</option>
          {STRIP_ORDER.map(k => <option key={k} value={k}>{STRIP_LABELS[k] === 'Added' ? 'New' : STRIP_LABELS[k]}</option>)}
        </select>
        <select style={select} value={filters.belief} onChange={e => setFilters(f => ({ ...f, belief: e.target.value }))}>
          <option value="">All beliefs</option>
          {options.beliefs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select style={select} value={filters.generator} onChange={e => setFilters(f => ({ ...f, generator: e.target.value }))}>
          <option value="">All generators</option>
          {options.generators.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <select style={select} value={filters.lane} onChange={e => setFilters(f => ({ ...f, lane: e.target.value }))}>
          <option value="">All lanes</option>
          {options.lanes.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <select style={select} value={sort} onChange={e => setSort(e.target.value as 'rank' | 'new_first')}>
          <option value="rank">Sort: rank</option>
          <option value="new_first">Sort: new this read first</option>
        </select>
        {filtering && (
          <button style={btn} onClick={() => setFilters(NO_FILTERS)}><X size={11} /> Clear filters</button>
        )}
      </div>

      {main.length === 0
        ? <DSEmptyState message={filtering
            ? 'Nothing matches these filters.'
            : 'No action has been triggered by the data yet. An action is only added when a read gives a reason.'} />
        : <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>{main.map(card)}</div>}

      <Collapsible title="Confirmed in earlier reads" count={olderConfirmed.length}>{olderConfirmed.map(card)}</Collapsible>
      <Collapsible title="Retired in earlier reads" count={olderRetired.length}>{olderRetired.map(card)}</Collapsible>
    </div>
  );
}
